import { find, findIndex, get, last, remove } from 'lodash';
import { getIdObjectOrNull, getWithDefault } from './common';
import {
  SINGER_PROFESSION_TYPE_ID,
  MEDIA_TAGS_TYPES_IDS,
  PRODUCTION_ADDED_IDENTIFIERS_IDS,
  EMPTY_INFO_MARK,
  CONTACTS_NAMES,
  TP,
} from '../constants/consts';
import { artistsPaths, companiesPaths, festivalPaths } from '../constants/routes';
import { getFestivalRoute } from '../containers/Festival/utils';
import { getCompanyRoute } from '../containers/Company/utils';
import { getArtistRoute } from '../containers/Artist/utils';

export const getCorrectTabValue = (queryTab, tabValues, defaultTab) =>
  Object.values(tabValues).includes(queryTab) ? queryTab : defaultTab;

const getQueryTabsForEditPages = (tabName, subTab) => {
  let queryTabs = '';
  if (tabName) {
    queryTabs = `?tab=${tabName}`;
    if (subTab) {
      queryTabs += `&${subTab?.name}=${subTab?.value}`;
    }
  }
  return queryTabs;
};

export const getRouterCompanyEditPath = (company, tabName, subTab) => ({
  path: companiesPaths.edit,
  as: `${getCompanyRoute(company)}/edit${getQueryTabsForEditPages(tabName, subTab)}`,
});

export const getRouterFestivalEditPath = (festival, tabName, subTab) => ({
  path: festivalPaths.edit,
  as: `${getFestivalRoute(festival)}/edit${getQueryTabsForEditPages(tabName, subTab)}`,
});

export const getRouterArtistEditPath = (profile, tabName, subTab) => ({
  path: artistsPaths.edit,
  as: `${getArtistRoute(profile)}/edit${getQueryTabsForEditPages(tabName, subTab)}`,
});

export const getArray = values => (Array.isArray(values) ? values : []);

export const checkCompletion = (completedSections, sectionName) =>
  Boolean(completedSections.find(name => name === sectionName));

export const BOX_OFFICE_CATEGORY = 'box_office';
export const POINT_OF_CONTACT_CATEGORY = 'point_of_contact';

export const EDITING_ENTITY_TYPES = {
  COMPANY: 'company',
  ARTIST: 'artist',
  FESTIVAL: 'festival',
};

export const EDITING_MEDIA_ENTITY_TYPES = {
  ...EDITING_ENTITY_TYPES,
  PRODUCTION: 'production',
};

export const setContactForUpdateAction = (name, value) => ({ name, ...value });

export const getCategory = name => {
  if (
    [CONTACTS_NAMES.BOX_OFFICE_PHONE, CONTACTS_NAMES.BOX_OFFICE_EMAIL, CONTACTS_NAMES.BOX_OFFICE_WEBSITE].includes(name)
  ) {
    return { contact_category: BOX_OFFICE_CATEGORY };
  }

  if ([CONTACTS_NAMES.POC_EMAIL, CONTACTS_NAMES.POC_PHONE].includes(name)) {
    return { contact_category: POINT_OF_CONTACT_CATEGORY };
  }

  return {};
};

export const getNewContactStructure = ({ name, value, typeId, privacyAttributes = [], isPrimary }) => ({
  name,
  contactType: { id: typeId },
  value,
  isPrimary,
  ...getCategory(name),
  privacyAttributes: privacyAttributes.map(pa => ({
    id: pa.id,
  })),
});

export const separateContactsForUpdate = (contactsValues, oldContacts) => {
  const newContacts = [];
  const updatedContacts = [];
  const deletedContacts = [];

  // eslint-disable-next-line no-unused-expressions
  contactsValues?.forEach(value => {
    if (value?.value) {
      if (value?.id) {
        updatedContacts.push({
          id: value?.id,
          value: value?.value,
          isPrimary: value?.isPrimary,
          ...getCategory(value?.name),
          privacyAttributes: value?.privacyAttributes.map(pa => ({
            id: pa?.id,
          })),
        });
      } else {
        newContacts.push(getNewContactStructure(value));
      }
    } else if (value?.id) {
      deletedContacts.push(value?.id);
    }
  });

  // eslint-disable-next-line no-unused-expressions
  oldContacts?.forEach(value => {
    const matchedContact = find(contactsValues, { id: value?.id });

    if (!matchedContact) {
      deletedContacts.push(value?.id);
    }
  });
  return { newContacts, updatedContacts, deletedContacts };
};

export const changeItemInObjectsArray = (array, item, param = 'id') => {
  const data = [...getArray(array)];
  data.splice(findIndex(array, { [param]: get(item, param) }), 1, item);
  return data;
};

export const removeTagFromMediaTags = (tags, tagId, tagTypeId) => {
  const updatedTags = [...getArray(tags)];
  remove(updatedTags, { tagId, tagType: { id: tagTypeId } });
  if (tagTypeId === MEDIA_TAGS_TYPES_IDS.PRODUCTION) {
    const taggedDate = find(tags, { tagType: { id: MEDIA_TAGS_TYPES_IDS.DATE } });
    if (taggedDate?.tagId) {
      remove(updatedTags, { tagId: taggedDate?.tagId });
    }
  }
  return updatedTags;
};

export const isReqRedMaskShown = production => {
  const { ARTIST, COMPANY, ADMIN } = PRODUCTION_ADDED_IDENTIFIERS_IDS;
  const addedById = production?.addedBy?.id;
  const approvedBy = production?.approvedBy;
  return addedById === ARTIST && !approvedBy.find(el => [COMPANY, ADMIN].includes(el.id));
};

export const canArtistEditProduction = (production, profileId) => {
  const isOwner =
    production?.forEntity === PRODUCTION_ADDED_IDENTIFIERS_IDS.ARTIST && production?.forEntityId === profileId;
  if (isOwner) {
    const { COMPANY, ADMIN } = PRODUCTION_ADDED_IDENTIFIERS_IDS;
    return ![COMPANY, ADMIN].includes(production?.approvedBy?.id);
  }
  return false;
};

export const isProductionType = (types, typeId) => Boolean(find(types, { id: typeId }));

export const getAcceptFormatsTypesForFiles = (type, formats) => formats?.map(f => `${type}/${f}`);

export const joinMediaFormats = (formats, separator = ', ') => formats?.map(f => f?.toUpperCase()).join(separator);

export const getProfessionRole = profession => (profession.isNew ? { name: profession?.name } : { id: profession?.id });

export const getRole = el =>
  el?.workRole?.isNew
    ? { workRole: { name: el?.workRole?.name } }
    : {
        workRole: { id: el?.workRole?.id },
        professionQualification: getIdObjectOrNull(el?.professionQualification?.id),
      };

export const getCastShapeForNewRecord = (castRecord, artistProfile) => ({
  profile: { id: artistProfile?.id },
  profession:
    castRecord?.type === 'profession' ? getProfessionRole(castRecord?.profession) : { id: SINGER_PROFESSION_TYPE_ID },
  ...(castRecord?.type === 'role' ? getRole(castRecord) : {}),
});

// update values NOT from form values but from casts array itself
// we need to send ONLY id for exist cast record
// casts = [cast records with & without id]
export const modifyIncomingCastRecordForSaving = (castRecord, artistProfile = {}) => {
  let readyToSendRecord;
  if (castRecord?.id) {
    readyToSendRecord = { id: castRecord?.id };
  } else {
    readyToSendRecord = getCastShapeForNewRecord(castRecord, artistProfile);
  }
  return readyToSendRecord;
};

const getCastValue = c =>
  c.map(el => ({
    profession: { id: el?.profession?.id },
    profile: { id: el?.profile?.id },
    workRole: { id: el?.workRole?.id },
  }));

export const isCastDirty = (initialCasts, curCast) => {
  const initialCast = getCastValue(initialCasts);
  const currentCast = getCastValue(curCast);
  return JSON.stringify(initialCast) !== JSON.stringify(currentCast);
};

export const getSeparateCastsByArtistId = (casts, artistProfileId) => {
  const artistsCast = [];
  const restCast = [];
  casts.forEach(cast => {
    if (cast?.profile?.id === artistProfileId) {
      artistsCast.push({ ...cast, type: cast?.workRole || cast?.workRoleReference ? 'role' : 'profession' });
    } else {
      restCast.push(cast);
    }
  });
  return [artistsCast, restCast];
};

export const getCastShapeForDisplayNewRecord = (option, artistProfileId) => ({
  type: option?.type,
  profile: { id: artistProfileId },
  profession:
    option?.type === 'profession' ? { id: option?.value, name: option?.label, isNew: option?.__isNew__ } : null,
  ...(option?.type === 'role'
    ? {
        workRole: { id: option?.value, name: option?.label, isNew: option?.__isNew__ },
        professionQualification: getIdObjectOrNull(option?.professionQualification?.id),
      }
    : {}),
});

export const isFocusedAddAnotherField = (index, values) =>
  index === values?.length - 1 && index > 0 && !last(values)?.value;

export const getMediaTagsWithoutEntityTag = (mediaTags, tagTypeId, entityId) => {
  const updatedTags = [...(mediaTags || [])];
  remove(updatedTags, { tagType: { id: tagTypeId }, tagId: entityId });
  return updatedTags;
};

const getTrimedValueForCheckDuplicates = (obj, path) =>
  getWithDefault(obj, path, '')
    ?.trim()
    ?.toLowerCase();

export const checkDuplicateName = (profile, confirmedData) => {
  const firstName = getTrimedValueForCheckDuplicates(profile, 'firstName', '');
  const lastName = getTrimedValueForCheckDuplicates(profile, 'lastName', '');
  const fullName = getTrimedValueForCheckDuplicates(profile, 'name', '');
  const firstValuesName = getTrimedValueForCheckDuplicates(confirmedData, 'firstName', '');
  const lastValuesName = getTrimedValueForCheckDuplicates(confirmedData, 'lastName', '');
  const fullValuesName = `${firstValuesName} ${lastValuesName}`;
  return (firstName === firstValuesName && lastName === lastValuesName) || fullName === fullValuesName;
};

export const getDateValues = (values, date) => ({
  day: values[date.day.name]?.value || null,
  month: values[date.month.name]?.value || null,
  year: values[date.year.name]?.value || null,
});

export const getCastRoleName = cast =>
  cast?.workRole?.original_name ||
  cast?.workRole?.name ||
  cast?.workRoleReference ||
  cast?.profession?.name ||
  cast?.professionReference ||
  EMPTY_INFO_MARK;

export const professionStickyHeaders = [
  {
    key: 'profession',
    value: `${TP}.FN_PROFESSION`,
    width: 245,
  },
  {
    key: 'crew',
    value: `${TP}.FN_ARTIST_NAME`,
    width: 200,
  },
  {
    key: 'select_all',
    value: '',
    width: 200,
  },
];

export const professionScrollableHeaders = [
  {
    key: 'performance_dates',
    value: `${TP}.FN_PERF_DATES`,
    colSpan: 22,
  },
];

export const rolesStickyHeaders = [
  {
    key: 'role',
    value: `${TP}.FN_ROLE`,
    width: 245,
  },
  {
    key: 'Artist',
    value: `${TP}.FN_ARTIST_NAME`,
    width: 200,
  },
  {
    key: 'select_all',
    value: '',
    width: 200,
  },
];

export const rolesScrollableHeaders = [
  {
    key: 'performance_dates',
    value: `${TP}.FN_PERF_DATES`,
    colSpan: 22,
  },
];

export const ensemblesStickyHeaders = [
  {
    key: 'ensemble',
    value: `${TP}.FN_ENSEMBLE`,
    width: 500,
  },
  {
    key: 'select_all',
    value: '',
    width: 200,
  },
];

export const ensembleScrollableHeaders = [
  {
    key: 'performance_dates',
    value: `${TP}.FN_PERF_DATES`,
    colSpan: 22,
  },
];
